import { useReducer, useCallback, useEffect } from 'react';

// Interview states
export const INTERVIEW_STATES = {
  INITIALIZING: 'initializing',
  PERMISSION_REQUEST: 'permission_request',
  READY_TO_START: 'ready_to_start',
  STARTING: 'starting',
  ACTIVE: 'active',
  PAUSED: 'paused',
  TERMINATING: 'terminating',
  COMPLETED: 'completed',
  ERROR: 'error'
};

// Interview actions
export const INTERVIEW_ACTIONS = {
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  START_INTERVIEW: 'START_INTERVIEW',
  PAUSE_INTERVIEW: 'PAUSE_INTERVIEW',
  RESUME_INTERVIEW: 'RESUME_INTERVIEW',
  TERMINATE_INTERVIEW: 'TERMINATE_INTERVIEW',
  COMPLETE_INTERVIEW: 'COMPLETE_INTERVIEW',
  SET_ERROR: 'SET_ERROR',
  RESET: 'RESET',
  TICK: 'TICK', // For timer countdown
  SET_TIME_REMAINING: 'SET_TIME_REMAINING' // For setting timer duration
};

// Initial state
const initialState = {
  currentState: INTERVIEW_STATES.INITIALIZING,
  permissions: {
    camera: false,
    microphone: false
  },
  isStarted: false,
  isCompleted: false,
  isTerminated: false,
  isActive: false,
  error: null,
  timeRemaining: 30 * 60, // 30 minutes in seconds
  startTime: null,
  endTime: null
};

// State reducer
const interviewReducer = (state, action) => {
  switch (action.type) {
    case INTERVIEW_ACTIONS.SET_PERMISSIONS:
      const newPermissions = { ...state.permissions, ...action.payload };
      const allPermissionsGranted = newPermissions.camera && newPermissions.microphone;
      
      return {
        ...state,
        permissions: newPermissions,
        currentState: allPermissionsGranted ? INTERVIEW_STATES.READY_TO_START : INTERVIEW_STATES.PERMISSION_REQUEST,
        error: null
      };

    case INTERVIEW_ACTIONS.START_INTERVIEW:
      if (state.currentState !== INTERVIEW_STATES.READY_TO_START) {
        return state; // Invalid transition
      }
      
      return {
        ...state,
        currentState: INTERVIEW_STATES.ACTIVE,
        isStarted: true,
        isActive: true,
        startTime: new Date(),
        error: null
      };

    case INTERVIEW_ACTIONS.PAUSE_INTERVIEW:
      if (state.currentState !== INTERVIEW_STATES.ACTIVE) {
        return state; // Invalid transition
      }
      
      return {
        ...state,
        currentState: INTERVIEW_STATES.PAUSED,
        isActive: false
      };

    case INTERVIEW_ACTIONS.RESUME_INTERVIEW:
      if (state.currentState !== INTERVIEW_STATES.PAUSED) {
        return state; // Invalid transition
      }
      
      return {
        ...state,
        currentState: INTERVIEW_STATES.ACTIVE,
        isActive: true
      };

    case INTERVIEW_ACTIONS.TERMINATE_INTERVIEW:
      if (!state.isStarted || state.isCompleted || state.isTerminated) {
        return state; // Invalid transition
      }
      
      return {
        ...state,
        currentState: INTERVIEW_STATES.TERMINATING,
        isActive: false,
        isTerminated: true
      };

    case INTERVIEW_ACTIONS.COMPLETE_INTERVIEW:
      return {
        ...state,
        currentState: INTERVIEW_STATES.COMPLETED,
        isActive: false,
        isCompleted: true,
        isTerminated: true,
        endTime: new Date()
      };

    case INTERVIEW_ACTIONS.SET_ERROR:
      return {
        ...state,
        currentState: INTERVIEW_STATES.ERROR,
        isActive: false,
        error: action.payload
      };

    case INTERVIEW_ACTIONS.RESET:
      return {
        ...initialState,
        timeRemaining: action.payload?.timeRemaining || initialState.timeRemaining
      };

    case INTERVIEW_ACTIONS.TICK:
      // Countdown timer - decrease by 1 second
      const newTimeRemaining = Math.max(0, state.timeRemaining - 1);
      return {
        ...state,
        timeRemaining: newTimeRemaining,
        // Auto-complete interview when time runs out
        ...(newTimeRemaining === 0 && state.isActive && {
          currentState: INTERVIEW_STATES.COMPLETED,
          isActive: false,
          isCompleted: true,
          endTime: new Date()
        })
      };

    case INTERVIEW_ACTIONS.SET_TIME_REMAINING:
      // Set time remaining directly (e.g., for different interview types)
      return {
        ...state,
        timeRemaining: action.payload
      };

    default:
      return state;
  }
};

/**
 * Custom hook for managing interview state with state machine logic
 * @param {Object} options - Configuration options
 * @param {function} options.onStateChange - Callback for state changes
 * @param {number} options.initialTimeRemaining - Initial time in seconds
 */
export const useInterviewState = ({ 
  onStateChange,
  initialTimeRemaining = 30 * 60 
} = {}) => {
  const [state, dispatch] = useReducer(interviewReducer, {
    ...initialState,
    timeRemaining: initialTimeRemaining
  });

  // Action creators
  const setPermissions = useCallback((permissions) => {
    dispatch({ type: INTERVIEW_ACTIONS.SET_PERMISSIONS, payload: permissions });
  }, []);

  const startInterview = useCallback(() => {
    const success = state.currentState === INTERVIEW_STATES.READY_TO_START;
    if (success) {
      dispatch({ type: INTERVIEW_ACTIONS.START_INTERVIEW });
    }
    return success;
  }, [state.currentState]);

  const pauseInterview = useCallback(() => {
    const success = state.currentState === INTERVIEW_STATES.ACTIVE;
    if (success) {
      dispatch({ type: INTERVIEW_ACTIONS.PAUSE_INTERVIEW });
    }
    return success;
  }, [state.currentState]);

  const resumeInterview = useCallback(() => {
    const success = state.currentState === INTERVIEW_STATES.PAUSED;
    if (success) {
      dispatch({ type: INTERVIEW_ACTIONS.RESUME_INTERVIEW });
    }
    return success;
  }, [state.currentState]);

  const terminateInterview = useCallback(() => {
    const success = state.isStarted && !state.isCompleted && !state.isTerminated;
    if (success) {
      dispatch({ type: INTERVIEW_ACTIONS.TERMINATE_INTERVIEW });
    }
    return success;
  }, [state.isStarted, state.isCompleted, state.isTerminated]);

  const completeInterview = useCallback(() => {
    dispatch({ type: INTERVIEW_ACTIONS.COMPLETE_INTERVIEW });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: INTERVIEW_ACTIONS.SET_ERROR, payload: error });
  }, []);

  const reset = useCallback((options = {}) => {
    dispatch({ type: INTERVIEW_ACTIONS.RESET, payload: options });
  }, []);

  const setTimeRemaining = useCallback((time) => {
    dispatch({ type: INTERVIEW_ACTIONS.SET_TIME_REMAINING, payload: time });
  }, []);

  // Computed properties
  const canStart = state.currentState === INTERVIEW_STATES.READY_TO_START;
  const canPause = state.currentState === INTERVIEW_STATES.ACTIVE;
  const canResume = state.currentState === INTERVIEW_STATES.PAUSED;
  const canTerminate = state.isStarted && !state.isCompleted && !state.isTerminated;
  const isWaitingForPermissions = state.currentState === INTERVIEW_STATES.PERMISSION_REQUEST;
  const isInitializing = state.currentState === INTERVIEW_STATES.INITIALIZING;
  const isTerminating = state.currentState === INTERVIEW_STATES.TERMINATING;
  const hasError = state.currentState === INTERVIEW_STATES.ERROR;

  // Status strings for UI
  const getStatusText = useCallback(() => {
    switch (state.currentState) {
      case INTERVIEW_STATES.INITIALIZING:
        return 'Initializing interview...';
      case INTERVIEW_STATES.PERMISSION_REQUEST:
        return 'Requesting permissions...';
      case INTERVIEW_STATES.READY_TO_START:
        return 'Ready to start interview';
      case INTERVIEW_STATES.STARTING:
        return 'Starting interview...';
      case INTERVIEW_STATES.ACTIVE:
        return 'Interview in progress';
      case INTERVIEW_STATES.PAUSED:
        return 'Interview paused';
      case INTERVIEW_STATES.TERMINATING:
        return 'Finishing interview...';
      case INTERVIEW_STATES.COMPLETED:
        return 'Interview completed';
      case INTERVIEW_STATES.ERROR:
        return 'Interview error';
      default:
        return 'Unknown state';
    }
  }, [state.currentState]);

  // Get interview duration
  const getDuration = useCallback(() => {
    if (!state.startTime) return 0;
    const endTime = state.endTime || new Date();
    return Math.floor((endTime - state.startTime) / 1000);
  }, [state.startTime, state.endTime]);

  // Get elapsed time in seconds (real-time during active interview)
  const getElapsedTime = useCallback(() => {
    if (!state.startTime) return 0;
    const now = state.endTime || new Date();
    return Math.floor((now - state.startTime) / 1000);
  }, [state.startTime, state.endTime]);

  // Format elapsed time as MM:SS
  const getFormattedElapsedTime = useCallback(() => {
    const elapsed = getElapsedTime();
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [getElapsedTime]);

  // Check if state transition is valid
  const canTransitionTo = useCallback((targetState) => {
    const currentState = state.currentState;
    
    // Define valid transitions
    const validTransitions = {
      [INTERVIEW_STATES.INITIALIZING]: [INTERVIEW_STATES.PERMISSION_REQUEST, INTERVIEW_STATES.READY_TO_START, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.PERMISSION_REQUEST]: [INTERVIEW_STATES.READY_TO_START, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.READY_TO_START]: [INTERVIEW_STATES.ACTIVE, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.STARTING]: [INTERVIEW_STATES.ACTIVE, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.ACTIVE]: [INTERVIEW_STATES.PAUSED, INTERVIEW_STATES.TERMINATING, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.PAUSED]: [INTERVIEW_STATES.ACTIVE, INTERVIEW_STATES.TERMINATING, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.TERMINATING]: [INTERVIEW_STATES.COMPLETED, INTERVIEW_STATES.ERROR],
      [INTERVIEW_STATES.COMPLETED]: [], // Terminal state
      [INTERVIEW_STATES.ERROR]: [INTERVIEW_STATES.INITIALIZING] // Can reset from error
    };

    return validTransitions[currentState]?.includes(targetState) || false;
  }, [state.currentState]);

  // Format time remaining for display (MM:SS)
  const getFormattedTimeRemaining = useCallback(() => {
    const minutes = Math.floor(state.timeRemaining / 60);
    const seconds = state.timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [state.timeRemaining]);

  // Timer countdown logic - only active when interview is active
  useEffect(() => {
    let timerInterval;
    
    if (state.isActive && state.timeRemaining > 0) {
      console.log(`⏰ Starting interview timer - ${Math.floor(state.timeRemaining / 60)}:${(state.timeRemaining % 60).toString().padStart(2, '0')} remaining`);
      
      timerInterval = setInterval(() => {
        dispatch({ type: INTERVIEW_ACTIONS.TICK });
      }, 1000); // Tick every second
    }

    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [state.isActive, state.timeRemaining]);

  // Call state change callback when state changes
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  return {
    // Current state
    ...state,
    
    // Actions
    setPermissions,
    startInterview,
    pauseInterview,
    resumeInterview,
    terminateInterview,
    completeInterview,
    setError,
    reset,
    setTimeRemaining,
    
    // Computed properties
    canStart,
    canPause,
    canResume,
    canTerminate,
    isWaitingForPermissions,
    isInitializing,
    isTerminating,
    hasError,
    
    // Helper methods
    getStatusText,
    getDuration,
    getElapsedTime,
    getFormattedElapsedTime,
    canTransitionTo,
    getFormattedTimeRemaining,
    
    // State constants for external use
    STATES: INTERVIEW_STATES
  };
};
